import { type Ref, useEffectEvent, useLayoutEffect } from "react";
import { eventNames, shopWindowTypes } from "@/modules/Constants";
import ClusterChecklist from "@/components/shared/ClusterChecklist";
import ShopWindowFooter from "@/components/shopwindow/ShopWindowFooter";
import ShopWindowHeader from "@/components/shopwindow/ShopWindowHeader";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

const REGION = "List Shop Window";

type ListShopWindowProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  hasHeader?: boolean;
  hasExitButton?: boolean;
  nodeRef?: Ref<HTMLDivElement>;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

export default function ListShopWindow({
  experienceState,
  config,
  hasHeader,
  hasExitButton,
  nodeRef,
  onApplicationEvent,
}: ListShopWindowProps) {
  const onDidMount = useEffectEvent(() => {
    onApplicationEvent({
      region: REGION,
      name: eventNames.SHOP_WINDOW_DID_MOUNT,
      type: shopWindowTypes.LIST,
    });
  });
  // componentDidMount: layout phase, after the children's mount effects.
  useLayoutEffect(() => {
    onDidMount();
  }, []);

  const onExit = () => {
    if (onApplicationEvent) {
      onApplicationEvent({
        name: eventNames.SHOP_WINDOW_EXIT,
        region: REGION,
        shopWindowConfig: config,
      });
    }
  };

  const experienceData = experienceState.experienceData;
  const hasChecks = false;
  const ctaLink = config.links ? config.links.cta : null;

  const header = hasHeader ? (
    <ShopWindowHeader
      src={experienceData.getLogoImageUrl()}
      hasExitButton={hasExitButton}
      onExit={onExit}
    />
  ) : null;

  let footer = null;

  if (ctaLink) {
    const isSelected = false;
    const isActive = true;

    footer = (
      <ShopWindowFooter
        experienceState={experienceState}
        config={config}
        links={config.links}
        isSelected={isSelected}
        isActive={isActive}
        hasButton={true}
        onApplicationEvent={onApplicationEvent}
      />
    );
  }

  return (
    <div
      className="list-shop-window tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-stretch tw:bg-[#fff]"
      ref={nodeRef}
    >
      {header}
      <ClusterChecklist
        experienceState={experienceState}
        clusterName={config.clusterName}
        hasChecks={hasChecks}
        onApplicationEvent={onApplicationEvent}
      />
      {footer}
    </div>
  );
}
